import { useTranslation } from 'react-i18next'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'

import {
  useLazyGetRoomInfoForAdminQuery,
  useUpdateRoomArchiveFromAdminMutation,
} from 'app/services/room.service'
import { BUTTONS, DROPDOWN, FORM, MODALS, PLACEHOLDERS, TABLE } from 'locales/locales'
import { FixMeLater } from 'types'

import useInfoModal from 'utils/hooks/useInfoModal'
import Loading from 'components/spinner/Loading'
import { IChannel } from 'types/group.types'
import KGrid from 'components/kendo/grid/KGrid'
import { GridColumn } from '@progress/kendo-react-grid'
import { useAppSelector } from 'app/hooks'
import { IRolePermissions, TeamAndChannelPermissions } from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import { icons } from 'utils/icons'
import Dropdown from 'components/kendo/dropdown/DropdownList'
import CustomIcon from 'components/icon/CustomIcon'
import { useLazyGetPaginatedChannelsQuery } from 'app/services/group.service'
import { IRoomForAdmin } from 'types/rooms.types'
import { useDispatch } from 'react-redux'
import { conversationActions } from 'app/slices/conversations.slice'
import ChannelInfoModal from 'features/settings/management/channel-management/channel-info/ChannelInfoModal'

interface DataStateFilter {
  field: string
  operator: string
  value: boolean | string
}

interface DataState {
  take: number
  skip: number
  filter: {
    logic: string
    filters: DataStateFilter[]
  }
  sort?: Array<{ field: string; dir: string }>
}
const initialDataState: DataState = {
  take: 10,
  skip: 0,
  filter: {
    logic: 'and',
    filters: [{ field: 'isArchived', operator: 'eq', value: false }],
  },
  sort: [],
}

const AdminChannelManagement = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [roomId, setRoomId] = useState('')
  const { openModal, closeModal, Modal } = useInfoModal()
  //Queries
  const [getChannelInfoById] = useLazyGetRoomInfoForAdminQuery()
  const [getAllChannels] = useLazyGetPaginatedChannelsQuery()
  const [updateRoomArchiveById] = useUpdateRoomArchiveFromAdminMutation()

  const [currentData, setCurrentData] = useState({ data: [] as FixMeLater[], totalCount: 1 })
  //Modal visible state
  const [channelInfoModalVisible, setChannelInfoModalVisible] = useState(false)
  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)
  const [isLoading, setIsLoading] = useState(false)

  const searchRef = useRef<HTMLInputElement | null>(null)
  const hasArchiveChannelPermission = isFlagSetIn(
    [TeamAndChannelPermissions.ARCHIVE_CHANNEL_SETTINGS],
    permissions.teamAndChannel,
  )
  const hasUpdateChannelPermission = isFlagSetIn(
    [TeamAndChannelPermissions.UPDATE_CHANNEL],
    permissions.teamAndChannel,
  )

  const [dataState, setDataState] = useState(initialDataState)

  useEffect(() => {
    fetchChannels()
  }, [dataState])

  const onCloseConfirmArchiveChannel = useCallback(() => {
    closeModal()
  }, [closeModal])

  const onClickChannel = useCallback((params: IRoomForAdmin) => {
    setRoomId(params._id)
    setChannelInfoModalVisible(true)
  }, [])

  const closeInfoModalVisible = useCallback(() => {
    setChannelInfoModalVisible(false)
  }, [])

  const fetchChannels = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await getAllChannels({ ...dataState, count: true })
      if (data.data) {
        const newArr = data.data?.channels.map((channel: IRoomForAdmin) => ({
          ...channel,
          actions: {
            actionKey: channel.isArchived
              ? { text: t(BUTTONS.UNARCHIVE), icon: icons.unarchiveNonFilledIcon }
              : { text: t(BUTTONS.ARCHIVE), icon: icons.archiveIcon },
          },
        }))
        setCurrentData({ data: newArr, totalCount: data.data?.totalCount || 0 })
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }, [dataState, t, getAllChannels])

  const onOkConfirmArchiveChannel = useCallback(
    async (roomId: string) => {
      try {
        closeModal()
        Loading.ref.show()
        const response = await getChannelInfoById({ roomId })
        await updateRoomArchiveById({
          roomId,
          isArchived: !response.data.team.isArchived,
        })
        setDataState((prevState) => ({ ...prevState }))
      } finally {
        Loading.ref.hide()
        dispatch(conversationActions.removeAllDraftAndFailedMessages({ channelId: roomId }))
      }
    },
    [closeModal, getChannelInfoById, updateRoomArchiveById, dispatch, setDataState],
  )

  const onToggleArchive = useCallback(
    async (channel: IChannel) => {
      const channelId = channel._id
      setRoomId(channelId)
      openModal({
        title: channel.isArchived ? t(MODALS.UNARCHIVE_TITLE) : t(MODALS.ARCHIVE_TITLE),
        description: channel.isArchived
          ? t(MODALS.UNARCHIVE_DESCRIPTION)
          : t(MODALS.ARCHIVE_DESCRIPTION),
        cancelTitle: t(BUTTONS.CANCEL) as string,
        okTitle: t(BUTTONS.SAVE) as string,
        onCancel: onCloseConfirmArchiveChannel,
        onOk: () => onOkConfirmArchiveChannel(channelId),
      })
    },
    [t, openModal, onCloseConfirmArchiveChannel, onOkConfirmArchiveChannel],
  )

  const ActionCell = useCallback(
    ({ dataItem }: FixMeLater) => {
      return (
        <td>
          <ButtonContainer>
            {hasUpdateChannelPermission && (
              <StyledCustomIcon>
                <CustomIcon
                  source={icons.editIcon}
                  onClick={() => {
                    onClickChannel(dataItem)
                  }}
                  iconPreset="grande"
                  alt="edit"
                />
              </StyledCustomIcon>
            )}
            {hasArchiveChannelPermission && (
              <StyledCustomIcon>
                <CustomIcon
                  iconPreset="grande"
                  source={dataItem.actions.actionKey.icon}
                  onClick={() => {
                    onToggleArchive(dataItem)
                  }}
                  alt="archive"
                />
              </StyledCustomIcon>
            )}
          </ButtonContainer>
        </td>
      )
    },
    [hasUpdateChannelPermission, hasArchiveChannelPermission],
  )

  const handleFilterChange = useCallback(
    (event: FixMeLater) => {
      setDataState({
        ...dataState,
        filter: event.filter,
      })
    },
    [dataState],
  )

  const onDataStateChange = useCallback((event: FixMeLater) => {
    setDataState((prev) => ({
      ...prev,
      skip: event.dataState.skip,
      take: event.dataState.take,
      sort: event.dataState.sort,
    }))
  }, [])

  const dropDownOptions = useMemo(
    () => [
      {
        title: t(DROPDOWN.ACTIVE_CHANNELS),
        value: JSON.stringify({ field: 'isArchived', operator: 'eq', value: false }),
      },
      {
        title: t(DROPDOWN.DEACTIVE_CHANNELS),
        value: JSON.stringify({ field: 'isArchived', operator: 'eq', value: true }),
      },
    ],
    [t],
  )

  const [activeChannelsFilter, setActiveChannelsFilter] = useState(dropDownOptions[0])

  const getIsArchivedFilter = useCallback(() => {
    return JSON.parse(activeChannelsFilter.value) as DataStateFilter
  }, [activeChannelsFilter])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.trim()
      const isArchivedFilter = getIsArchivedFilter()

      if (!searchValue) {
        setDataState((prev) => ({
          ...prev,
          filter: { logic: 'and', filters: [isArchivedFilter] as never },
          skip: 0,
        }))
        return
      }

      setDataState((prev) => ({
        ...prev,
        filter: {
          logic: 'and',
          filters: [
            isArchivedFilter,
            {
              logic: 'or',
              filters: [
                { field: 'name', operator: 'contains', value: searchValue },
                { field: 'groupName', operator: 'contains', value: searchValue },
              ],
            },
          ] as never,
        },
        skip: 0,
      }))
    },
    [getIsArchivedFilter],
  )

  const handleDropdownChange = useCallback(
    (e: FixMeLater) => {
      const parsed = JSON.parse(e.value) as DataStateFilter

      setActiveChannelsFilter({
        title: parsed.value ? dropDownOptions[1].title : dropDownOptions[0].title,
        value: e.value,
      })

      // search'i temizle
      if (searchRef.current) searchRef.current.value = ''

      // sadece dropdown filtresi
      setDataState((prev) => ({
        ...prev,
        filter: { logic: 'and', filters: [parsed] as never },
        take: 10,
        skip: 0,
      }))
    },
    [dropDownOptions],
  )

  const handleReloadClick = useCallback(() => {
    fetchChannels()
  }, [fetchChannels])

  return (
    <Container>
      <Wrapper>
        <ContentContainer>
          <HeaderBox>
            <HeaderTitle>{t(TABLE.TEAM_CHANNELS)}</HeaderTitle>
            <HeaderActions>
              <ReloadButton onClick={handleReloadClick}>
                {isLoading ? (
                  <LoadingSpinner />
                ) : (
                  <img
                    className="refresh"
                    height={15}
                    src={icons.refreshIcon.src}
                    srcSet={icons.refreshIcon.srcset}
                  />
                )}
              </ReloadButton>
            </HeaderActions>
          </HeaderBox>

          <FilterContainer>
            <FilterGroup>
              <FilterLabel>{t(FORM.FILTER)}:</FilterLabel>
              <Dropdown
                style={{ width: '250px' }}
                options={dropDownOptions}
                id="channel-filter-dropdown"
                selectedOption={activeChannelsFilter}
                onChangeOption={handleDropdownChange}
              />
            </FilterGroup>
            <SearchInput
              ref={searchRef}
              placeholder={t(PLACEHOLDERS.SEARCH_PLACEHOLDER)}
              onChange={handleSearchChange}
            />
          </FilterContainer>

          <TableWrapper>
            <KGrid
              loading={isLoading}
              datasource={currentData}
              dataState={dataState}
              pageable={true}
              total={currentData.totalCount | 0}
              filterable={false}
              editMode={false}
              sortable={true}
              onFilterChange={handleFilterChange}
              onDataStateChange={onDataStateChange}
              style={{ height: '100%' }}
            >
              <GridColumn field="name" title={t(TABLE.CHANNEL_NAME)}></GridColumn>
              <GridColumn field="groupName" title={t(TABLE.CHANNEL_TEAM_NAME)}></GridColumn>
              <GridColumn
                filterable={false}
                sortable={true}
                field="createdByUserId"
                title={t(TABLE.CHANNEL_CREATOR)}
              />
              <GridColumn
                field="actions"
                sortable={false}
                filterable={false}
                title={t(TABLE.CHANNEL_ACTIONS)}
                cells={{
                  data: (props) => <ActionCell {...props} />,
                }}
              />
            </KGrid>
          </TableWrapper>
        </ContentContainer>
      </Wrapper>
      {channelInfoModalVisible && (
        <ChannelInfoModal
          roomId={roomId}
          fetchChanels={fetchChannels}
          onClose={closeInfoModalVisible}
        />
      )}
      {Modal}
    </Container>
  )
}

export default AdminChannelManagement

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`

const ContentContainer = styled.div`
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  background-color: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  flex: 1;
`

const HeaderBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e6e6e6;
`

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 500;
  margin: 0;
  color: #333;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const ReloadButton = styled.button`
  width: 30px;
  height: 30.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
`

const TableWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 20px;
  border-bottom: 1px solid #e6e6e6;
  background-color: #f9f9f9;
  justify-content: space-between;
`

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
  width: 200px;
  font-size: 14px;

  &::placeholder {
    color: #888;
  }

  &:focus {
    outline: none;
    border-color: #129fdb;
    box-shadow: 0 0 0 2px rgba(18, 159, 219, 0.1);
  }
`
const FilterLabel = styled.div`
  font-size: 14px;
  color: #333;
  margin-right: 10px;
`

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
`

const StyledCustomIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    border-radius: 28px;
    background-color: #e0e0e0;
  }
  padding: 4px 6px;
  cursor: pointer;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 6px;
  margin-right: 10px;
  flex-wrap: wrap;
`

const LoadingSpinner = styled.div`
  width: 15px;
  height: 15px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`
